<template>
  <div id="map-wrapper">
    <div class="bar">
      <div class="left">
        <div
          id="geocoder"
          class="geocoder"
        ></div>
        <h3 class="cases">
          <div>
            {{
              $t(counterText, {
                arg: counterTextArg,
                days: days,
                cases: displayTotal,
                caseword: $tc("case", displayTotal),
                modifier: modifier
              })
            }}
          </div>
        </h3>
      </div>
      <div class="update">
        <p class="update-text">
          {{ $t("last_updated") }}:
          {{ formatAMPM(timestamp.hours, timestamp.minutes) }}
          {{ timestamp.month }}. {{ timestamp.day }}, {{ timestamp.year }} HKT
        </p>
      </div>
    </div>
    <div class="high-risk">
      <div class="high-risk-wrapper">
        <div
          v-if="controlsVisible"
          class="title"
        >{{ $t("high_risk_districts") }}:</div>
        <div
          class="markers"
          ref="highRiskWrapper"
        >
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>
    </div>
    <div class="scale">
      <Scale
        :minimum="scaleMin"
        :maximum="scaleMax"
      />
    </div>

    <div
      class="filters"
      v-if="controlsVisible"
    >
      <MapFilters
        :rawmax="rawMaxDistance"
        :slider="showSlider"
        v-on:distance="measureDistance($event)"
        v-on:status="updateFilters($event)"
      />
    </div>
    <mapbox
      :access-token="token"
      :map-options="{
        style: 'mapbox://styles/willemdehaes/ckdjs2d5m01hb1imutv2yyxfq',
        center: [114.1151, 22.3569],
        zoom: settings.initialZoom,
        maxBounds: [
          [113.5, 22],
          [114.8, 22.7]
        ]
      }"
      :nav-control="{ 
        show: true, 
        position: 'bottom-right',
        options: {
          showCompass: true,
          showZoom: true
        } 
       }"
      :geolocate-control="{
        show: true,
        position: 'bottom-right',
        options: {
          countries: 'hk',
          showAccuracyCircle: false
        }
      }"
      @map-init="initialized"
      @map-load="loaded"
      @map-zoom="handleZoom"
      @map-moveend="updateCalc"
      @map-mouseenter:buildings="mouseEnter"
      @map-mouseleave:buildings="mouseLeft"
      @map-click:buildings="addBuildingDetails"
      @geolocate-error="geolocateError"
      @geolocate-geolocate="geolocate"
      
    />
  </div>
</template>

<script>
import Mapbox from "mapbox-gl-vue";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import MapboxLanguage from "@mapbox/mapbox-gl-language";
import * as turf from "@turf/turf";
import mapboxgl from "mapbox-gl";
import * as d3 from "d3";
// import { districts, buildings, timestamp } from "../data/data";
import DistrictMarker from "./DistrictMarker";
import CaseCounter from "./CaseCounter";
import DetailedCaseCounter from "./DetailedCaseCounter";
import Scale from "./Scale";
import MapFilters from "./MapFilters";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import "mapbox-gl/dist/mapbox-gl.css";
export default {
  components: {
    Mapbox,
    // eslint-disable-next-line vue/no-unused-components
    DistrictMarker,
    // eslint-disable-next-line vue/no-unused-components
    CaseCounter,
    // eslint-disable-next-line vue/no-unused-components
    DetailedCaseCounter,
    // eslint-disable-next-line vue/no-unused-components
    MapboxGeocoder,
    // eslint-disable-next-line vue/no-unused-components
    MapboxLanguage,
    Scale,
   MapFilters
  },
  data() {
    return {
      token:
        "pk.eyJ1IjoiaGt1bWFwYm94IiwiYSI6ImNrZHBxZ2RmMzBnaHMzNGp6dmV6OXpvNjMifQ.VaBXgB9roWrFvsOaDsIQ0w",
        //"pk.eyJ1Ijoid2lsbGVtZGVoYWVzIiwiYSI6ImNqbmdqZ2hnYzAzb3ozd3F0aHcxM29idTkifQ.KCJrgwzjDOeW19ps9wyW2Q",
      mapLoaded: false,
       filters: {
        mall: false,
        restaurant: false,
        fiveday: false,
        distance: false
      },
      rawMaxDistance: 100,
      currentTotal: 0,
      hoveredBuildingId: null,
      minCasesInView: 1,
      scaleMin: 0,
      scaleMax: 0,
      maxCasesInView: 100,
      distanceFilter: 5,
      mapboxLanguage: {},
      activeDistrict: "",
      activePopupType: "",
      districtMarkers: [],
      settings: {
        zoomThreshold: 10.7,
        initialZoom: 10.5
      },
      zoom: 10.5
    };
  },
  props: {
    rawDistricts: Array,
    rawBuildings: Array,
    timestamp: Object
  },
  created() {
    this.map = null; // here we define map as non-reactive
    this.activePopup = null;
    this.geocoder = null;
  },
  computed: {
    modifier() {
      const modifierAry = [];
      if (this.filters.restaurant) {
        modifierAry.push("restaurants");
      }
      if (this.filters.mall) {
        modifierAry.push("malls");
      }
      if (modifierAry.length === 0) {
        return "";
      }
      const modifierKey = modifierAry.join("_and_");
      return this.$t("modifiers." + modifierKey);
    },
    activeLocale() {
      return this.$i18n.locale;
    },
    showSlider() {
      return this.filters.distance;
    },
    activeDistrictKey() {
      return this.activeDistrict.replace(/ /g, "_").replace("&", "And");
    },
    districtCenters() {
      return this.rawDistricts.map();
    },
    days() {
      if (this.filters.fiveday) {
        return 5;
      } else {
        return 14;
      }
    },
    counterText() {
      if (this.filters.distance) {
        return "counter_distance";
      } else {
        return "counter_district";
      }
    },
    counterTextArg() {
      if (this.filters.distance) {
        return this.distanceFilter;
      } else {
        if (!this.detailZoom) {
          return this.$t("HK");
        } else if (this.anyFilterActive() || !this.activeDistrict) {
          return this.$t("area");
        } else {
          return this.$t("districts." + this.activeDistrictKey);
        }
      }
    },
    minDistrictCases() {
      const mini = Math.min(
        ...this.districts.map(district => district.center.properties.cases)
      );
      return mini;
    },
    maxDistrictCases() {
      return Math.max(
        ...this.districts.map(district => district.center.properties.cases)
      );
    },
    detailZoom() {
      return this.zoom > this.settings.zoomThreshold;
    },
    controlsVisible() {
      return this.mapLoaded && this.detailZoom && this.geocoder;
    },
    highRisk() {
      const districtsCopy = [...this.districts];
      const districtsSorted = districtsCopy.sort(
        (a, b) => b.center.properties.cases - a.center.properties.cases
      );
      const districtsFiltered = districtsSorted.slice(0, 3);
      return districtsFiltered;
    },
    displayTotal() {
      if (!this.detailZoom) {
        return this.hkTotalCases;
      } else if (this.anyFilterActive() || !this.activeDistrict) {
        return this.currentTotal;
      } else {
        const d = this.districts.find(
          district => district.name === this.activeDistrictKey
        );
        return d.center.properties.cases;
      }
    },
    hkTotalCases() {
      const total = this.buildings([]).reduce(
        (total, building) => total.concat(building.cases),
        []
      );
      const l = new Set(total).size;
      return l;
    },
    districts() {
      const districtCases = this.rawDistricts.map(
        district => district.center.properties.cases
      );
      const colorScale = d3
        .scaleQuantize()
        .domain(d3.extent(districtCases))
        .range(["#49A669", "#84A765", "#BFA760", "#D98258", "#F25D50"]);
      const districts = this.rawDistricts.map(district => {
        district.center.properties.color = colorScale(
          district.center.properties.cases
        );
        return district;
      });

      return districts;
    }
  },
  watch: {
    activeLocale: function(val) {
      const updatedStyle = this.mapboxLanguage.setLanguage(
        this.map.getStyle(),
        val
      );
      this.map.setStyle(updatedStyle);
      const placeholder = this.$t("search_a_district_landmark_or_location");
      this.geocoder.setPlaceholder(placeholder);
      this.geocoder.setLanguage(val);
    },
    filters: function() {
      this.updateCalc(this.map);
      const center = this.map.getCenter();
      this.map.flyTo([center.lng + 0.00001, center.lat + 0.00001], {
        maxDuration: 0.1
      });
    }
  },
  methods: {
    anyFilterActive() {
      return Object.keys(this.filters).some(k => this.filters[k]);
    },
    scaleMinUpdate() {
      if (this.detailZoom) {
        this.scaleMin = this.minCasesInView;
      } else {
        this.scaleMin = this.minDistrictCases;
      }
    },
    scaleMaxUpdate() {
      if (this.detailZoom) {
        this.scaleMax = this.maxCasesInView;
      } else {
        this.scaleMax = this.maxDistrictCases;
      }
    },
    updateFilters(filterUpdate) {
      this.filters = Object.assign({}, this.filters, filterUpdate);
    },
    calcRawMaxDistance(bounds) {
      const dist = turf.distance(bounds[0], bounds[1]);
      this.rawMaxDistance = dist / 2.5;
    },
    setTotalCases(cases) {
      const total = cases.reduce(
        (total, building) => total.concat(building),
        []
      );
      const l = new Set(total).size;
      this.currentTotal = l;
    },
    splitArrayString(aryString) {
      const ary = aryString
        .replace("[", "")
        .replace("]", "")
        .replace(/ /g, "")
        .replace(/"/g, "")
        .split(",");
      return ary.map(x => parseInt(x));
    },
    createGeoJSON(building, name) {
      // NOTE: Sometimes building.lat/lng are null, and this breaks the map if not filtered out
      const lng = building.lng !== null ? building.lng : 0;
      ``;
      const lat = building.lat !== null ? building.lat : 0;

      const geo = {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [lng, lat]
        },
        properties: {
          title: name,
          name: building.name,
          name_zh: building.name_zh,
          count: building.count,
          cases: building.cases,
          date: building.date,
          source: building,
          address: building.address,
          district: building.district,
          color: building.color,
          tp: turf.point([lng, lat])
        }
      };
      return geo;
    },
    initialized(map) {
      this.map = map;
    },
    daysAgo(day) {
      const dateAry = day.split("/");
      const lastCaseDate = new Date(dateAry[2], dateAry[1] - 1, dateAry[0]);
      const currentDate = new Date();
      return Math.floor(
        (Date.UTC(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          currentDate.getDate()
        ) -
          Date.UTC(
            lastCaseDate.getFullYear(),
            lastCaseDate.getMonth(),
            lastCaseDate.getDate()
          )) /
          (1000 * 60 * 60 * 24)
      );
    },
    buildings(colorScalebuildings) {
      let buildingCases = [];
      if (colorScalebuildings.length === 0) {
        buildingCases = this.rawBuildings.map(
          building => building.cases.length
        );
      } else {
        buildingCases = colorScalebuildings.map(building => {
          return building.properties.count;
        });
      }
      this.maxCasesInView = Math.max(...buildingCases);
      this.minCasesInView = Math.min(...buildingCases);

      const colorScale = d3
        .scaleQuantize()
        .domain(d3.extent(buildingCases)) // pass the array of the number of cases in each building
        .range(["#49A669", "#84A765", "#BFA760", "#D98258", "#F25D50"]);

      const noPOIFilterss = !(
        this.filters["mall"] || this.filters["restaurant"]
      );
      const filterBuildings = this.rawBuildings.filter(building => {
        let recentEnough = true;
        if (this.filters["fiveday"]) {
          recentEnough = this.daysAgo(building.date) < 5;
        }
        return recentEnough && (noPOIFilterss || this.filters[building.kind]);
      });
      const buildings = filterBuildings.map(building => {
        building.count = building.cases.length;
        building.color = colorScale(building.cases.length);
        return building;
      });
      return buildings;
    },
    initiateBuildingData() {
      const buildingsGeoJSON = {
        type: "geojson",
        generateId: true,
        data: {
          type: "FeatureCollection",
          features: []
        }
      };
      this.buildings([]).forEach(building => {
        const name = (building.name + "-" + building.district).replace(
          /\s/g,
          "_"
        );
        const geoPoint = this.createGeoJSON(building, name);
        buildingsGeoJSON.data.features.push(geoPoint);
      });
      this.map.addSource("buildings", buildingsGeoJSON, true);
      this.map.addLayer({
        id: "buildings",
        interactive: true,
        type: "circle",
        source: "buildings",
        minzoom: this.settings.zoomThreshold,
        layout: {
          "circle-sort-key": ["get", "count"]
        },
        paint: {
          "circle-color": ["get", "color"],
          "circle-stroke-color": "#f2f2f2",
          "circle-stroke-width": 0.5,
          "circle-radius": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            8,
            5
          ]
        }
      });
    },
    updateBuildingData(buildingsInFocus) {
      const buildingsGeoJSON = {
        type: "FeatureCollection",
        features: []
      };

      this.buildings(buildingsInFocus).forEach(building => {
        const name = (building.name + "-" + building.district).replace(
          /\s/g,
          "_"
        );
        const geoPoint = this.createGeoJSON(building, name);
        buildingsGeoJSON.features.push(geoPoint);
      });

      this.map.getSource("buildings").setData(buildingsGeoJSON);
    },
    loaded(map) {
      this.mapboxLanguage = new MapboxLanguage({
        defaultLanguage: "zh",
        supportedLanguages: ["en", "zh"]
      });
      map.addControl(this.mapboxLanguage);
      const geocoder = new MapboxGeocoder({
        accessToken: this.token,
        collapsed: true,
        placeholder: this.$t("search_a_district_landmark_or_location"),
        countries: "hk",
        marker: false,
        types: "poi,address,neighborhood,locality,place,district,postcode",
        mapboxgl: mapboxgl
      });
      document.getElementById("geocoder").appendChild(geocoder.onAdd(map));
      this.geocoder = geocoder;

      map.addSource("single-point", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });

      map.addLayer({
        id: "point",
        source: "single-point",
        type: "symbol",
        minzoom: this.settings.zoomThreshold,
        layout: {
          "icon-image": "locator",
          "icon-size": 1.5,
          "icon-allow-overlap": true
        }
      });

      // Listen for the `result` event from the Geocoder
      // `result` event is triggered when a user makes a selection
      //  Add a marker at the result's coordinates
      geocoder.on("result", function(e) {
        map.getSource("single-point").setData(e.result.geometry);
      });

      this.districts.forEach(district => {
        const markerHTML = document.createElement("div");
        markerHTML.innerHTML = "<div><div>";
        markerHTML.id = district.name + "_marker";

        const marker = new mapboxgl.Marker(markerHTML)
          .setLngLat(district.center.geometry.coordinates)
          .addTo(map);
        const districtMarker = new DistrictMarker({
          propsData: { district: district, zoom: this.zoom, highRisk: false }
        }).$mount(marker.getElement().firstElementChild);
        districtMarker.map = map;
        districtMarker.zoom = this.zoom;
        districtMarker.geocoder = this.geocoder;
        this.districtMarkers.push(districtMarker);
      });
      const highRiskNodes = this.$refs.highRiskWrapper.childNodes;
      this.highRisk.forEach((district, index) => {
        const districtMarker = new DistrictMarker({
          propsData: { district: district, zoom: this.zoom, highRisk: true }
        }).$mount(highRiskNodes[index]);
        districtMarker.map = map;
        districtMarker.zoom = this.zoom;
        districtMarker.geocoder = this.geocoder;
        this.districtMarkers.push(districtMarker);
      });
      this.initiateBuildingData();
      const bounds = map.getBounds().toArray();
      this.calcRawMaxDistance(bounds);
      this.updateCalc(this.map);
      this.$nextTick(() => {
        this.mapLoaded = true;
      });
    },
    geolocateError(control, positionError) {
      console.log(positionError);
    },
    geolocate(control, position) {
      const userPosition = {
        type: "Point",
        coordinates: [position.coords.longitude, position.coords.latitude]
      };
      this.map.getSource("single-point").setData(userPosition);
      console.log(
        `User position: ${position.coords.latitude}, ${position.coords.longitude}`
      );
    },
    handleZoom(map) {
      this.zoom = map.getZoom();
      if (this.activePopup && !this.detailZoom) {
        this.activePopup.remove();
        this.activePopupType = "";
        this.hoveredBuildingId = null;
      }
      if (this.anyFilterActive() && !this.detailZoom) {
        this.updateFilters({
          mall: false,
          restaurant: false,
          fiveday: false,
          distance: false
        });
      }
      this.districtMarkers.forEach(districtMarker => {
        districtMarker.zoom = this.zoom;
      });
    },
    measureDistance(val) {
      this.distanceFilter = val;
      this.updateCalc(this.map);
    },
    updateCalc(map) {
      const center = map.getCenter();
      const bounds = map.getBounds();
      let currentTotals = [];
      let coord = map.getSource("single-point")._data.coordinates;
      if (typeof myVar !== "undefined" || (coord && !bounds.contains(coord))) {
        const newMarker = {
          type: "Point",
          coordinates: [center.lng, center.lat]
        };
        this.map.getSource("single-point").setData(newMarker);
        coord = [center.lng, center.lat];
      }
      const visibleBuildings = map.queryRenderedFeatures({
        layers: ["buildings"]
      });
      let minDistance = 10000;
      let closestDistrict = "";
      visibleBuildings.forEach(building => {
        const distanceToCenter = turf.distance(building.geometry.coordinates, [
          center.lng,
          center.lat
        ]);
        if (distanceToCenter < minDistance) {
          minDistance = distanceToCenter;
          closestDistrict = building.properties.district;
        }
        this.activeDistrict = closestDistrict;
        const ary = this.splitArrayString(building.properties.cases);

        if (!this.filters.distance || distanceToCenter < this.distanceFilter) {
          currentTotals = currentTotals.concat(ary);
        }
      });
      this.setTotalCases(currentTotals);
      this.updateBuildingData(visibleBuildings);
      this.scaleMinUpdate();
      this.scaleMaxUpdate();
      this.geocoder._inputEl.blur();
      this.geocoder._inputEl.disabled = false;

      this.calcRawMaxDistance(bounds.toArray());
    },
    addBuildingDetails(map, e) {
      if (e.features) {
        const point = e.features[0];
        const coordinates = point.geometry.coordinates.slice();
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }
        this.activePopup = new mapboxgl.Popup()
          .setLngLat({ lng: coordinates[0], lat: coordinates[1] })
          .setHTML('<div><div id="vue-popup-content"></div>')
          .addTo(map);

        const properties = point.properties;
        new DetailedCaseCounter({
          propsData: {
            district: false,
            name: properties.name,
            name_zh: properties.name_zh,
            color: properties.color,
            total: properties.count,
            cases: properties.cases,
            lastday: properties.date
          }
        }).$mount("#vue-popup-content");
        this.activePopupType = "click";
      }
    },
    mouseEnter(map, e) {
      map.getCanvas().style.cursor = "pointer";
      if (e.features) {
        const point = e.features[0];
        const coordinates = point.geometry.coordinates.slice();
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }
        this.activePopup = new mapboxgl.Popup()
          .setLngLat({ lng: coordinates[0], lat: coordinates[1] })
          .setHTML('<div><div id="vue-popup-content"></div>')
          .addTo(map);

        const properties = point.properties;
        new CaseCounter({
          propsData: {
            district: false,
            name: properties.name,
            name_zh: properties.name_zh,
            color: properties.color,
            cases: properties.count,
            i18n: this.$i18n
          }
        }).$mount("#vue-popup-content");
        this.activePopupType = "hover";

        if (this.hoveredBuildingId) {
          map.setFeatureState(
            { source: "buildings", id: this.hoveredBuildingId },
            { hover: false }
          );
        }
        this.hoveredBuildingId = point.id;
        map.setFeatureState(
          { source: "buildings", id: this.hoveredBuildingId },
          { hover: true }
        );
      }
    },
    mouseLeft(map) {
      if (this.activePopupType === "hover") {
        this.activePopup.remove();
        this.activePopupType = "";
      }
      map.getCanvas().style.cursor = "";
      if (this.hoveredBuildingId) {
        map.setFeatureState(
          { source: "buildings", id: this.hoveredBuildingId },
          { hover: false }
        );
      }
      this.hoveredBuildingId = null;
    },
    formatAMPM(hours, minutes) {
      let ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12;
      minutes = minutes < 10 ? "0" + minutes : minutes;
      let strTime = hours + ":" + minutes + " " + ampm;

      return strTime;
    }
  }
};
</script>

<style scoped lang="scss">
#map {
  width: 100%;
  height: 100%;
  height: 100%;
}

#map-wrapper {
  position: fixed;
  width: 100%;
  height: 100vh;
  display: block;
}

.bar {
  position: fixed;
  top: 38px;
  padding: 6px;
  background: white;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  z-index: 10;
  @include shadow(0, 1px, 5px, 0.4);

  .left {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    top: 38px;
    // search box in app.vue
    #geocoder.geocoder {
      max-width: 350px;
      width: 30vw;
      height: 100%;
      display: inline-block;
    }
    // display text
    h3.cases {
      margin: 0;
      display: inline-block;
      color: $hku-black;
      padding-left: 2vw;
      width: 40vw;
      font-weight: 600;
      line-height: 1.4;
      // .count {
      // font-weight: 600;
      // color: $hku-red;
      // }
    }
  }

  //timestamp
  .update {
    font-weight: 400;
    font-size: 13px;
    width: 20vw;
    // min-width: 180px;
    color: $hku-gray-1;
    display: inline-block;
    margin-top: 2px;

    p {
      margin: 0;
      text-align: right;
      vertical-align: middle;
    }
  }
}

@media screen and (max-width: $breakpoint-wide) {
  .bar {
    top: 38px;
  }
  .bar .left h3.cases {
    font-size: 1em;
  }
}

@media screen and (max-width: $breakpoint-tablet) {
  .mapboxgl-ctrl-icon {
    bottom: 25px;
  }

  .bar {
    top: 32px;
    display: block;
    .left {
      display: block;

      // search box in app.vue
      #geocoder.geocoder {
        width: 100%;
        max-width: inherit;
        display: block;
      }
      // display text
      h3.cases {
        margin: 10px 0 2px 0;
        width: 100%;
        display: block;
        padding-left: 0;
        font-size: 1em;
        .count {
          font-weight: 600;
        }
      }
    }

    //timestamp
    .update {
      display: block;
      width: 100%;
      font-size: 12px;
      color: $hku-gray-1;
      margin-top: 0;

      p {
        text-align: left;
      }
    }
  }
}

.high-risk {
  position: absolute;
  z-index: 10;
  left: 10px;
  bottom: 10px;
  @media screen and (max-width: $breakpoint-tablet) {
    display: none;
  }
}
.high-risk-wrapper {
  display: block;
  text-align: left;

  .title {
    color: $hku-gray-2;
    text-transform: uppercase;
    font-size: 15px;
    letter-spacing: 0.02em;
    font-weight: 700;
    margin-bottom: 5px;
  }
}

.scale {
  position: absolute;
  right: 10px;
  top: 78px;
  z-index: 2;

  @media screen and (max-width: $breakpoint-tablet) {
    position: relative;
    height: 0;
    top: 10px;
    width: 100%;
    left: 0;
  }
}

.filters {
  position: absolute;
  left: 10px;
  top: 78px;
  z-index: 2;

  @media screen and (max-width: $breakpoint-tablet) {
    top: inherit;
    bottom: 25px;
    width: 100%;
    left: 10px;
    right: 10px;
  }
}
</style>
